// Full-screen playback of a timeline episode, from the playhead (from the start if the playhead is at the end). It
// waits for every picture and face sprite to be decoded first, then plays; pause and play carry on from where it stopped.
import { useEffect, useState } from "react";
import { imagesInRange, timelineFrameAt } from "../../engine/timelineFrame";
import { decodeImages } from "../../library/images";
import { useLibrary } from "../../library/store";
import { displayDuration, type TimelineEpisode } from "../../model/timeline";
import { FrameSvg } from "../../render/FrameSvg";
import { FitFrame, Icon, Layer, Page, TopBar, fmt, useIdleClock, useTimelinePlayback } from "../common";

export function FullPlayer({ ep, from, onClose }: { ep: TimelineEpisode; from: number; onClose: (t: number) => void }) {
  const lib = useLibrary();
  const total = displayDuration(ep);
  const pb = useTimelinePlayback(ep, lib);
  const [at, setAt] = useState(from >= total - 0.1 ? 0 : from); // where it is when paused
  const [ended, setEnded] = useState(false);
  const [loading, setLoading] = useState(true);
  const clock = useIdleClock();
  useEffect(() => { if (pb.t !== null) setAt(pb.t); }, [pb.t]);
  const start = async (t: number) => {
    setEnded(false);
    await decodeImages(imagesInRange(ep, t, total)).catch(() => {});
    setLoading(false);
    void pb.play(t, () => { setAt(total); setEnded(true); });
  };
  useEffect(() => { void start(at); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const t = pb.t ?? at;
  const close = () => { pb.stop(); onClose(Math.min(t, total)); };
  return (
    <Layer>
      <Page dark className="player">
        <TopBar onBack={close} back="close" title={ep.title}
          right={<button className="icon-btn" onClick={() => (pb.playing ? pb.stop() : void start(ended ? 0 : t))} aria-label={pb.playing ? "Pause" : "Play"}><Icon name={pb.playing ? "pause" : "play"} /></button>} />
        <FitFrame>
          <FrameSvg id="full-player" frame={timelineFrameAt(ep, Math.min(t, total - 0.001), clock)} />
          {loading && <div className="frame-busy"><span className="spinner big light" /><b>Loading…</b></div>}
          {ended && <button className="replay-big" onClick={() => void start(0)}><Icon name="replay" size={26} /> Watch again</button>}
        </FitFrame>
        <div className="fs-tools">
          <div className="progress"><span style={{ width: `${(t / (total || 1)) * 100}%` }} /></div>
          <div className="fs-time">{fmt(t)} / {fmt(total)}</div>
        </div>
      </Page>
    </Layer>
  );
}
